/**
 * PURPOSE: Detalle de una prueba de Atletismo (columna derecha).
 * RESPONSIBILITIES: Cargar `GET .../pruebas/:id` y mostrar cabecera, visibilidad, pestañas de series,
 *   el editor de la serie abierta, "Armar final", "Inscritos sin serie", la clasificación de la
 *   prueba, "Oficializar" y "Reabrir". En combinadas muestra sus sub-pruebas.
 *
 *   CONCURRENCIA. Toda acción de escritura manda `expectedRevision = prueba.revision`. Un 409
 *   `REVISION_CONFLICT` muestra el banner "Otro usuario guardó cambios en esta prueba" con
 *   "Recargar", que vuelve a pedir el detalle y descarta el borrador.
 *
 *   ACCIONES ESTRUCTURALES CON CAMBIOS PENDIENTES. Armar la final, agregar/quitar inscritos y
 *   oficializar cambian las filas de las series: con marcas sin guardar se piden guardar o descartar
 *   primero, para que el borrador nunca apunte a filas que ya no existen.
 *
 *   EDITAR LA PRUEBA DESDE LA PRUEBA (mientras no sea oficial). Íconos chicos por fila: mover a otra serie, reemplazar
 *   (pista con carril: otro toma su carril) y quitar (popover; vuelve a inscritos sin serie). "+" en la cabecera de la
 *   serie y en los carriles libres: deportista (pruebas individuales) o institución (postas, como equipo). "+" junto a las
 *   pestañas agrega una serie; "×" en una serie vacía la quita. Serie terminada o prueba oficial: todo bloqueado con el
 *   motivo en el tooltip y un aviso. En las sub-pruebas de combinada no se quita ni reemplaza una fila con resultado.
 * COLLABORATORS: AtletismoResultadosArea.jsx, RoundEditor.jsx, hooks/useAthleticsResults.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/PruebaDetailPanel.jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useQueryClient } from "react-query";
import { FiAlertTriangle, FiArrowLeft, FiChevronLeft, FiGitMerge, FiPlus, FiRefreshCw, FiX } from "react-icons/fi";

import {
  athleticsResultsKeys,
  useAthleticsPrueba,
  useAthleticsResultsActions,
} from "../../../hooks/useAthleticsResults.js";
import { parseAthleticsError } from "../../../utils/athleticsResultsErrors.js";
import { describePodiumSync } from "../labels.js";
import {
  COMBINED_RESULT_REASON,
  describeEntryResult,
  entryHasResult,
  isRemovableSeries,
  moveTargetRounds,
  roundName,
  seriesRoundsOf,
  supportsSeries,
  usesLanes,
} from "../placement.js";
import AddAthleteModal from "./AddAthleteModal.jsx";
import AddTeamModal from "./AddTeamModal.jsx";
import { ConfirmPopover, IconAction } from "./IconAction.jsx";
import { DISCARD_CHANGES_OPTIONS, useConfirmDialog } from "./ConfirmDialog.jsx";
import CombinedPanel from "./CombinedPanel.jsx";
import MoveEntryModal from "./MoveEntryModal.jsx";
import OfficializeModal from "./OfficializeModal.jsx";
import PruebaHeader from "./PruebaHeader.jsx";
import PruebaStandingsTable from "./PruebaStandingsTable.jsx";
import RoundEditor from "./RoundEditor.jsx";
import SheetDownloads from "./SheetDownloads.jsx";
import UnassignedPanel, { unassignedKey } from "./UnassignedPanel.jsx";
import VisibilityPanel from "./VisibilityPanel.jsx";
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  RoundStatusChip,
  Spinner,
  secondaryButtonClass,
} from "./ui.jsx";

/** Serie que se abre por defecto: la que está en curso, si no la primera sin terminar, si no la última. */
export const pickDefaultRoundId = (rounds) => {
  const list = Array.isArray(rounds) ? rounds : [];
  if (!list.length) return null;
  const inProgress = list.find((round) => round.status === "in_progress");
  if (inProgress) return inProgress.id;
  const open = list.find((round) => round.status !== "done");
  if (open) return open.id;
  return list[list.length - 1].id;
};

const PruebaDetailPanel = ({
  idevent,
  idsport,
  pruebaId,
  permissions,
  onDirtyChange,
  onOpenSubPrueba,
  parent,
  onBackToParent,
}) => {
  const queryClient = useQueryClient();
  const detailQuery = useAthleticsPrueba({ idevent, idsport, pruebaId });
  const actions = useAthleticsResultsActions({ idevent, idsport });
  const { confirm, dialog } = useConfirmDialog();
  const [selectedRoundId, setSelectedRoundId] = useState(null);
  const [editorDirty, setEditorDirty] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [reloading, setReloading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [officialOpen, setOfficialOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addPreset, setAddPreset] = useState(null);
  const [teamState, setTeamState] = useState(null);
  const [moveState, setMoveState] = useState(null);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(editorDirty);
  }, [editorDirty, onDirtyChange]);

  useEffect(
    () => () => {
      if (onDirtyChange) onDirtyChange(false);
    },
    [onDirtyChange]
  );

  const detail = detailQuery.data || null;
  const prueba = detail?.prueba || null;
  const rounds = useMemo(() => (Array.isArray(detail?.rounds) ? detail.rounds : []), [detail]);
  const activeRound = useMemo(() => {
    const byId = (id) => rounds.find((round) => Number(round.id) === Number(id));
    return byId(selectedRoundId) || byId(pickDefaultRoundId(rounds)) || null;
  }, [rounds, selectedRoundId]);

  const official = prueba?.status === "official";
  const canWrite = permissions?.canWrite === true;
  const readOnly = !canWrite || official;
  const readOnlyReason = official ? "Prueba oficial: solo lectura" : "Tu rol solo permite consultar";
  const expectedRevision = prueba?.revision;

  const handleConflict = useCallback((parsed) => setConflict(parsed), []);
  const searchAthleteCandidates = actions.searchCandidates;
  const candidatePruebaId = prueba?.id;
  // Estable entre renders: la ventana de búsqueda depende de esta función (si cambiara, reiniciaría su espera).
  const searchCandidates = useCallback(
    (query) => searchAthleteCandidates({ pruebaId: candidatePruebaId, query }),
    [candidatePruebaId, searchAthleteCandidates]
  );
  const searchTeams = actions.searchTeamCandidates;
  // Estable entre renders: la ventana de instituciones la pide una vez al abrir.
  const searchTeamCandidates = useCallback(() => searchTeams({ pruebaId: candidatePruebaId }), [candidatePruebaId, searchTeams]);
  const refetchDetail = detailQuery.refetch;

  const run = async (kind, task, successMessage, key = null) => {
    setBusy(kind);
    setBusyKey(key);
    try {
      const result = await task();
      if (successMessage) toast.success(successMessage);
      return result;
    } catch (error) {
      const parsed = parseAthleticsError(error);
      if (parsed.isConflict) setConflict(parsed);
      else {
        toast.error(parsed.message);
        if (parsed.isLocked) refetchDetail();
      }
      return null;
    } finally {
      setBusy(null);
      setBusyKey(null);
    }
  };

  const requireSaved = (action) => {
    if (!editorDirty) return true;
    toast.error(`Guarda o descarta los cambios de la serie antes de ${action}.`);
    return false;
  };

  const reload = async () => {
    setReloading(true);
    try {
      await refetchDetail();
      queryClient
        .invalidateQueries(athleticsResultsKeys.pruebas(idevent, idsport))
        .catch(() => undefined);
    } finally {
      setReloading(false);
      setConflict(null);
      setReloadNonce((value) => value + 1);
    }
  };

  const selectRound = async (roundId) => {
    if (activeRound && Number(activeRound.id) === Number(roundId)) return;
    if (editorDirty && !(await confirm(DISCARD_CHANGES_OPTIONS))) return;
    setSelectedRoundId(roundId);
  };

  const changeVisibility = (change) =>
    run(
      "visibility",
      () => actions.setVisibility({ pruebaId: prueba.id, visibility: change }),
      "Visibilidad actualizada."
    );

  const openOfficialize = () => {
    if (!requireSaved("oficializar")) return;
    setOfficialOpen(true);
  };

  const confirmOfficialize = async () => {
    const result = await run(
      "official",
      () => actions.officialize({ pruebaId: prueba.id, expectedRevision }),
      "Prueba oficializada."
    );
    setOfficialOpen(false);
    // `podiumSync` (aditivo): si el podio quedó registrado en Resultados individuales (FTR-0057) o
    // por qué no. No toca el medallero general, así que el aviso nunca lo menciona.
    const podium = describePodiumSync(result?.podiumSync);
    if (podium) {
      if (podium.ok) toast.success(podium.message);
      else toast(podium.message, { duration: 6000 });
    }
  };

  const reopen = async () => {
    const ok = await confirm({
      title: "Reabrir prueba",
      message:
        "La prueba dejará de ser oficial y volverá a estar en edición. En Hayllis 2.0 se verá según su visibilidad. ¿Continuar?",
      confirmLabel: "Reabrir",
      tone: "danger",
    });
    if (!ok) return;
    await run("reopen", () => actions.reopen({ pruebaId: prueba.id, expectedRevision }), "Prueba reabierta.");
  };

  const buildFinal = async () => {
    if (!requireSaved("armar la final")) return;
    const ok = await confirm({
      title: "Armar final",
      message:
        "Se armará la final con los 8 mejores tiempos de las series; los carriles se sortean por grupos de clasificación (100 m: 1.º-4.º en 3-6, 5.º-6.º en 2 y 7, 7.º-8.º en 1 y 8; 200 m: 5-7, 3-4-8, 1-2; 400 m: 4-7, 3 y 8, 1 y 2). Si la final ya existe y aún no tiene marcas, se vuelve a armar.",
      confirmLabel: "Armar final",
    });
    if (!ok) return;
    const result = await run(
      "final",
      () => actions.buildFinal({ pruebaId: prueba.id, expectedRevision }),
      "Final armada."
    );
    const finalRound = (result?.rounds || []).find((round) => round.type === "final");
    if (finalRound) setSelectedRoundId(finalRound.id);
  };

  /** Agrega un inscrito sin serie a la serie abierta; con `lane` (tablero de carriles), directo a ese carril. */
  const addEntry = (item, lane = null) => {
    if (!activeRound || !requireSaved("agregar inscritos")) return;
    run(
      "entry",
      () =>
        actions.addEntry({
          pruebaId: prueba.id,
          roundId: activeRound.id,
          entryId: item.entryId,
          teamId: item.teamId,
          lane,
          expectedRevision,
        }),
      `${item.name || "Inscrito"} agregado a ${activeRound.label || "la serie"}${lane ? ` (carril ${lane})` : ""}.`,
      unassignedKey(item)
    );
  };

  /**
   * Agregar un deportista a la prueba (inscripción + serie). `preset` ({ roundId, lane, mode, targetRowId }) abre la ventana
   * en esa serie y carril ("Reemplazar a X", "+" de un carril libre). El modal muestra cualquier rechazo del servidor.
   */
  const openAdd = (preset = null) => {
    if (!requireSaved("agregar deportistas")) return;
    setAddPreset(preset);
    setAddOpen(true);
  };

  const placedText = ({ round, lane, replace, swap }) =>
    `${roundName(round)}${lane ? ` (carril ${lane})` : ""}${replace ? "; el anterior salió de la serie" : swap ? "; el anterior quedó sin carril" : ""}`;

  const submitAdd = async ({ idacreditation, roundId, lane, candidate, round, ...occupied }) => {
    setBusy("athlete");
    try {
      await actions.addAthlete({ pruebaId: prueba.id, idacreditation, roundId, lane, expectedRevision, ...occupied });
      toast.success(`${candidate.name} agregado a ${placedText({ round, lane, ...occupied })}.`);
      setSelectedRoundId(roundId);
      return { ok: true };
    } catch (error) {
      const parsed = parseAthleticsError(error);
      if (parsed.isConflict) {
        setConflict(parsed);
        return { ok: false, close: true };
      }
      if (parsed.isLocked) refetchDetail();
      return { ok: false, message: parsed.message };
    } finally {
      setBusy(null);
    }
  };

  /** Agregar una institución como equipo de posta (reutiliza su equipo sin serie o crea uno sin relevistas). */
  const openAddTeam = (preset = null) => {
    if (!requireSaved("agregar equipos")) return;
    setTeamState({ preset });
  };

  const submitTeam = async ({ institutionId, roundId, lane, candidate, round, ...occupied }) => {
    setBusy("team");
    try {
      await actions.addTeam({ pruebaId: prueba.id, institutionId, roundId, lane, expectedRevision, ...occupied });
      toast.success(`${candidate.institution.abrev || candidate.institution.name} agregado a ${placedText({ round, lane, ...occupied })}.`);
      setSelectedRoundId(roundId);
      return { ok: true };
    } catch (error) {
      const parsed = parseAthleticsError(error);
      if (parsed.isConflict) {
        setConflict(parsed);
        return { ok: false, close: true };
      }
      if (parsed.isLocked) refetchDetail();
      return { ok: false, message: parsed.message };
    } finally {
      setBusy(null);
    }
  };

  /** "+" de la serie / de un carril libre y "Reemplazar a X": deportista o, en postas, institución. */
  const openAddFor = (preset) => (prueba?.format === "PST" ? openAddTeam(preset) : openAdd(preset));
  const searchAtLane = (lane) => openAddFor({ roundId: activeRound?.id, lane });
  const openReplace = (entry) => openAddFor({ roundId: activeRound?.id, lane: entry.lane, mode: "replace", targetRowId: entry.id });

  /** Agregar una serie: la serie única pasa a Serie 1 y la nueva copia N°, horario y cámara de llamados. */
  const addSeries = async () => {
    if (!requireSaved("agregar una serie")) return;
    const result = await run("series", () => actions.addRound({ pruebaId: prueba.id, expectedRevision }), null);
    const heats = seriesRoundsOf(result?.rounds);
    const created = heats.length ? heats.reduce((last, round) => (Number(round.number) > Number(last.number) ? round : last)) : null;
    if (created) {
      toast.success(`${roundName(created)} agregada (mismo N° y horario).`);
      setSelectedRoundId(created.id);
    }
  };

  /** Quitar una serie vacía: las demás se renumeran (si queda una sola sin final, vuelve a serie única). */
  const removeSeries = async (round) => {
    if (!requireSaved("quitar una serie")) return;
    const result = await run("series", () => actions.removeRound({ pruebaId: prueba.id, roundId: round.id, expectedRevision }), `${roundName(round)} quitada.`);
    if (result && Number(selectedRoundId) === Number(round.id)) setSelectedRoundId(null);
  };

  /** Mover una fila de la serie abierta a otra serie de la misma prueba. */
  const openMove = (entry) => {
    if (!activeRound || !requireSaved("mover filas")) return;
    setMoveState({ entry, sourceRoundId: activeRound.id });
  };

  const submitMove = async ({ targetRoundId, lane, order, swap, swapRowId, confirmResult, entry, target }) => {
    setBusy("move");
    try {
      await actions.moveEntry({
        pruebaId: prueba.id,
        roundId: moveState.sourceRoundId,
        rowId: entry.id,
        targetRoundId,
        lane,
        order,
        swap,
        swapRowId,
        confirmResult,
        expectedRevision,
      });
      toast.success(`${entry.participant?.name || "Participante"} ${swap ? "intercambiado" : "movido"} a ${roundName(target)}${lane ? ` (carril ${lane})` : ""}.`);
      setSelectedRoundId(targetRoundId);
      return { ok: true };
    } catch (error) {
      const parsed = parseAthleticsError(error);
      if (parsed.isConflict) {
        setConflict(parsed);
        return { ok: false, close: true };
      }
      if (parsed.isLocked) refetchDetail();
      return { ok: false, message: parsed.message };
    } finally {
      setBusy(null);
    }
  };

  /** Quitar una fila de la serie (ya confirmado en el popover de la fila; con resultado viaja `confirmResult`). */
  const removeRow = async (entry, { confirmResult = false } = {}) => {
    if (!activeRound || !requireSaved("quitar inscritos")) return;
    const name = entry.participant?.name || "este inscrito";
    await run(
      "entry",
      () =>
        actions.removeEntry({
          pruebaId: prueba.id,
          roundId: activeRound.id,
          rowId: entry.id,
          expectedRevision,
          confirmResult,
        }),
      `${name} quitado de la serie: queda en inscritos sin serie.`
    );
  };

  if (detailQuery.isLoading) {
    return (
      <Card>
        <LoadingState label="Cargando prueba..." />
      </Card>
    );
  }

  if (detailQuery.isError || !prueba) {
    return (
      <Card>
        {parent ? (
          <button
            type="button"
            onClick={onBackToParent}
            className="m-4 inline-flex items-center gap-1 text-sm font-bold text-indigo-700"
          >
            <FiChevronLeft aria-hidden="true" /> {parent.label}
          </button>
        ) : null}
        <ErrorState
          message={
            detailQuery.isError
              ? parseAthleticsError(detailQuery.error).message
              : "La prueba no devolvió datos."
          }
          onRetry={() => refetchDetail()}
        />
      </Card>
    );
  }

  const isCombined = prueba.format === "PCB";
  // Las sub-pruebas de una combinada heredan su visibilidad: el panel solo vive en la combinada.
  const isSubPrueba = Boolean(prueba.parentId) || Boolean(parent);
  const canBuildFinal = prueba.format === "CPT" && prueba.hasFinal === true && canWrite && !official;
  // Relevos y combinadas no admiten agregar deportistas desde aquí (los relevos se arman con equipos y relevistas).
  const supportsAddAthlete = prueba.format !== "PST" && prueba.format !== "PCB";
  const canMoveRows = canWrite && !official && Boolean(activeRound) && moveTargetRounds(rounds, activeRound).length > 0;
  const isRelay = prueba.format === "PST";
  const canEdit = canWrite && !official;
  // Motivos de bloqueo (tooltip de cada ícono y aviso sobre la tabla).
  const officialReason = "Prueba oficial: reábrela para editarla.";
  const busyReason = busy ? "Espera a que termine la acción en curso." : null;
  const roundLock =
    activeRound?.status === "done" ? `${roundName(activeRound)} está terminada: cambia su estado a "En curso" para agregar, quitar o mover.` : null;
  const rowLock = roundLock || busyReason;
  const series = seriesRoundsOf(rounds);
  const singleDone = series.length === 1 && series[0].type === "single" && series[0].status === "done";
  const addSeriesReason = official
    ? officialReason
    : singleDone
      ? "La serie única está terminada: cambia su estado para agregar otra."
      : series.length >= 12
        ? "La prueba ya tiene el máximo de series."
        : busyReason;
  const canAddHere = canEdit && !rowLock;
  const rowActions = (entry) => {
    const hasResult = entryHasResult(entry);
    return {
      roundLabel: roundName(activeRound),
      lockReason: rowLock,
      removeLockReason: isSubPrueba && hasResult ? COMBINED_RESULT_REASON : null,
      hasResult,
      resultText: describeEntryResult(entry),
      onMove: canMoveRows ? () => openMove(entry) : null,
      onReplace: usesLanes(prueba) && entry.lane ? () => openReplace(entry) : null,
      onRemove: (options) => removeRow(entry, options),
    };
  };
  const addLabel = `${isRelay ? "Agregar institución" : "Agregar deportista"} a ${roundName(activeRound)}`;
  const headerActions =
    canWrite && (supportsAddAthlete || isRelay) && activeRound ? (
      <IconAction
        icon={FiPlus}
        label={addLabel}
        disabledReason={official ? officialReason : rowLock}
        onClick={() => openAddFor({ roundId: activeRound.id })}
      />
    ) : null;
  const moveSource = moveState ? rounds.find((round) => Number(round.id) === Number(moveState.sourceRoundId)) || null : null;
  const hasFinalRound = prueba.hasFinal === true && rounds.some((round) => round.type === "final");
  const hasRanking = Array.isArray(detail.ranking) && detail.ranking.length > 0;
  // Concursos con corte a los 8 mejores: la clasificación trae solo a los finalistas y el ranking a todos.
  const fieldCut = prueba.format === "SHL" && hasRanking;

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        {parent ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-indigo-100 bg-indigo-50/70 px-4 py-2 text-sm">
            <span className="min-w-0 truncate">
              <span className="font-semibold text-indigo-700">{parent.label}</span>
              <span className="mx-1.5 text-slate-400">/</span>
              <span className="font-semibold text-slate-700">{prueba.name || prueba.label}</span>
            </span>
            <button
              type="button"
              onClick={onBackToParent}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-indigo-700"
            >
              <FiArrowLeft aria-hidden="true" /> Ir a {parent.label}
            </button>
          </div>
        ) : null}

        <PruebaHeader
          prueba={prueba}
          parentLabel={isSubPrueba ? parent?.label || null : null}
          warnings={detail.warnings}
          canOfficialize={permissions?.canOfficialize === true}
          canReopen={permissions?.canReopen === true}
          dirty={editorDirty}
          busy={busy}
          onOfficialize={openOfficialize}
          onReopen={reopen}
          downloads={
            <SheetDownloads
              key={isCombined ? "combined" : activeRound?.id ?? "none"}
              idevent={idevent}
              idsport={idsport}
              prueba={prueba}
              round={isCombined ? null : activeRound}
            />
          }
        />

        {conflict ? (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 border-b border-rose-200 bg-rose-50 px-4 py-3"
          >
            <p className="flex items-start gap-2 text-sm font-bold text-rose-800">
              <FiAlertTriangle className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                Otro usuario guardó cambios en esta prueba. Recarga para ver la versión actual; tus
                cambios sin guardar se perderán.
              </span>
            </p>
            <button type="button" onClick={reload} disabled={reloading} className={secondaryButtonClass}>
              {reloading ? <Spinner /> : <FiRefreshCw aria-hidden="true" />} Recargar
            </button>
          </div>
        ) : null}

        {isSubPrueba ? (
          <p className="border-b border-slate-100 bg-slate-50/60 px-4 py-2 text-xs font-semibold text-slate-500">
            La publicación en Hayllis 2.0 se hereda de la combinada{parent?.label ? ` (${parent.label})` : ""}.
          </p>
        ) : (
          <VisibilityPanel
            prueba={prueba}
            disabled={!canWrite}
            busy={busy === "visibility"}
            publicEnabled={permissions?.publicEnabled === true}
            onChange={changeVisibility}
          />
        )}

        {isCombined ? (
          <CombinedPanel combined={detail.combined} onOpenEvent={(event) => onOpenSubPrueba(event.pruebaId)} />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 pt-3">
              <div role="tablist" aria-label="Series" className="flex flex-wrap gap-1">
                {rounds.map((round) => {
                  const selected = activeRound && Number(activeRound.id) === Number(round.id);
                  const removable = canEdit && supportsSeries(prueba) && isRemovableSeries(round, rounds);
                  return (
                    <span key={round.id} className="inline-flex items-center">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={Boolean(selected)}
                        onClick={() => selectRound(round.id)}
                        className={`-mb-px inline-flex items-center gap-2 rounded-t-lg border-b-4 px-3 py-2 text-sm font-bold transition ${
                          selected
                            ? "border-indigo-600 text-indigo-700"
                            : "border-transparent text-slate-500 hover:text-slate-800"
                        }`}
                      >
                        {round.label || `Serie ${round.number || ""}`}
                        <RoundStatusChip status={round.status} />
                      </button>
                      {removable ? (
                        <ConfirmPopover
                          disabled={Boolean(busyReason)}
                          title={`¿Quitar la ${roundName(round)}?`}
                          message="Está vacía. Las demás series se renumeran; si queda una sola y la prueba no tiene final, vuelve a ser serie única."
                          confirmLabel="Quitar serie"
                          onConfirm={() => removeSeries(round)}
                          trigger={(props) => (
                            <IconAction {...props} size="sm" icon={FiX} tone="rose" label={`Quitar la ${roundName(round)}`} disabledReason={busyReason} className="-ml-1" />
                          )}
                        />
                      ) : null}
                    </span>
                  );
                })}
                {canWrite && supportsSeries(prueba) && series.length ? (
                  <IconAction icon={FiPlus} label="Agregar serie" disabledReason={addSeriesReason} onClick={addSeries} className="self-center" />
                ) : null}
                {detailQuery.isFetching ? (
                  <span className="self-center px-2 text-slate-400">
                    <Spinner className="h-3.5 w-3.5" />
                  </span>
                ) : null}
              </div>
              {canBuildFinal ? (
                <button
                  type="button"
                  onClick={buildFinal}
                  disabled={Boolean(busy)}
                  className={`${secondaryButtonClass} mb-2`}
                  title="Arma la final con los 8 mejores tiempos de las series"
                >
                  {busy === "final" ? <Spinner /> : <FiGitMerge aria-hidden="true" />}
                  Armar final (8 mejores tiempos)
                </button>
              ) : null}
            </div>

            {activeRound ? (
              <RoundEditor
                key={`${prueba.id}:${activeRound.id}:${reloadNonce}`}
                prueba={prueba}
                round={activeRound}
                readOnly={readOnly}
                readOnlyReason={readOnlyReason}
                canRemove={canEdit}
                saveRound={actions.saveRound}
                onDirtyChange={setEditorDirty}
                onConflict={handleConflict}
                onLocked={refetchDetail}
                rowActions={rowActions}
                showWaPoints={Boolean(prueba.parentId)}
                unassigned={canEdit ? detail.unassigned : []}
                onAddEntry={canAddHere ? addEntry : undefined}
                onSearchAtLane={canAddHere && (supportsAddAthlete || isRelay) ? searchAtLane : undefined}
                headerActions={headerActions}
                notice={canEdit ? roundLock : null}
              />
            ) : (
              <EmptyState
                title="Sin series"
                description="Esta prueba todavía no tiene series ni final armadas en el servidor."
              />
            )}

            {canWrite && !official && activeRound ? (
              <UnassignedPanel
                unassigned={detail.unassigned}
                roundLabel={activeRound.label}
                disabled={Boolean(busy) || Boolean(roundLock)}
                busyKey={busy === "entry" ? busyKey : null}
                onAdd={addEntry}
              />
            ) : null}
          </>
        )}
      </Card>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
          <h4 className="text-sm font-black uppercase tracking-wide text-indigo-700">
            {hasFinalRound || fieldCut
              ? `Clasificación final · puntos y medallas${fieldCut ? " (8 finalistas)" : ""}`
              : "Clasificación de la prueba"}
          </h4>
          {official ? <Badge tone="green">Oficial</Badge> : <Badge tone="amber">Provisional</Badge>}
        </div>
        <PruebaStandingsTable
          standings={detail.standings}
          emptyLabel={
            hasFinalRound
              ? "Los puntos y las medallas salen solo de la final: todavía no tiene resultados."
              : undefined
          }
        />
      </Card>

      {hasRanking ? (
        <Card className="overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
            <h4 className="text-sm font-black uppercase tracking-wide text-indigo-700">
              {fieldCut ? "Ranking · mejor marca (todos los atletas)" : "Ranking · mejor tiempo (serie o final)"}
            </h4>
            <Badge tone="slate">Sin puntos ni medallas</Badge>
          </div>
          <PruebaStandingsTable standings={detail.ranking} ranking />
        </Card>
      ) : null}

      <AddAthleteModal
        open={addOpen}
        prueba={prueba}
        rounds={rounds}
        defaultRoundId={activeRound?.id ?? null}
        preset={addPreset}
        searchCandidates={searchCandidates}
        onSubmit={submitAdd}
        onClose={() => setAddOpen(false)}
      />
      {isRelay ? (
        <AddTeamModal
          open={Boolean(teamState)}
          prueba={prueba}
          rounds={rounds}
          defaultRoundId={activeRound?.id ?? null}
          preset={teamState?.preset ?? null}
          searchCandidates={searchTeamCandidates}
          onSubmit={submitTeam}
          onClose={() => setTeamState(null)}
        />
      ) : null}
      <MoveEntryModal
        open={Boolean(moveState)}
        prueba={prueba}
        rounds={rounds}
        sourceRound={moveSource}
        entry={moveState?.entry || null}
        onSubmit={submitMove}
        onClose={() => setMoveState(null)}
      />
      <OfficializeModal
        open={officialOpen}
        prueba={prueba}
        standings={detail.standings}
        rounds={rounds}
        loading={busy === "official"}
        onCancel={() => setOfficialOpen(false)}
        onConfirm={confirmOfficialize}
      />
      {dialog}
    </div>
  );
};

export default PruebaDetailPanel;
